import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Link } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import ExternalLink from '../../../../../../ui-kitchen/src/svg/icons/external-link.svg';
import { getRetinaImage, handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { ACQUISITION_CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexPlatinumReferAFriend.scss';

const AmexPlatinumReferAFriend = ({
  Analytics,
  awsFolderName,
  baseRequestUrl,
  mgmConfig,
}) => {
  if (!mgmConfig || mgmConfig.hide_section) return null;

  const {
    badge,
    title,
    copy,
    card_image: cardImage,
    image_name: imageName,
    external_link_aria: ariaText,
    image,
    cta = {},
    section_id: sectionId = 'mgm',
  } = mgmConfig;

  const sanitizedBadge = sanitizeString(badge);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);

  const getImageSrc = () => {
    if (typeof cardImage === 'string' && cardImage) {
      return `${baseRequestUrl}${awsFolderName}/images/${cardImage}`;
    }

    if (imageName) {
      return `${baseRequestUrl}${awsFolderName}/images/${imageName}`;
    }

    if (typeof image === 'string' && image) return image;

    if (image?.desktop || image?.mobile) {
      if (typeof image?.desktop === 'object' || typeof image?.mobile === 'object') {
        const imageAsset = image?.desktop?.retina
          || image?.desktop?.regular
          || image?.mobile?.retina
          || image?.mobile?.regular;
        return imageAsset ? `${baseRequestUrl}${awsFolderName}/images/${imageAsset}` : '';
      }

      const imageAsset = getRetinaImage(image?.desktop, image?.mobile, true);
      return imageAsset ? `${baseRequestUrl}${awsFolderName}/images/${imageAsset}` : '';
    }

    return '';
  };

  const imageSrc = getImageSrc();

  const handleCtaClick = () => {
    handleMktAnalytics(Analytics?.event, ACQUISITION_CTA_EVENT_NAME, {
      action_name: cta?.action_name || 'MGM - Click',
    });
  };

  return (
    <section className="AmexPlatinumReferAFriend">
      <div className="container">
        <div className="AmexPlatinumReferAFriend__inner">
          <div className="AmexPlatinumReferAFriend__content">
            <hgroup>
              {badge && (
                <p
                  className="AmexPlatinumReferAFriend__badge"
                  dangerouslySetInnerHTML={{ __html: sanitizedBadge }}
                />
              )}
              {title && (
                <h2
                  id={sectionId}
                  className="AmexPlatinumReferAFriend__title"
                  dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
                />
              )}
            </hgroup>
            {copy && (
              <p
                className="AmexPlatinumReferAFriend__copy"
                dangerouslySetInnerHTML={{ __html: sanitizedCopy }}
              />
            )}
          </div>
          {imageSrc && (
            <img
              className="AmexPlatinumReferAFriend__image"
              src={imageSrc}
              alt=""
            />
          )}
          {cta?.label && cta?.url && (
            <Link
              href={cta.url}
              className="AmexPlatinumReferAFriend__cta"
              onClick={handleCtaClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              {cta.label}
              <Icon component={ExternalLink} ariaLabel={ariaText} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

AmexPlatinumReferAFriend.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  mgmConfig: PropTypes.shape({
    badge: PropTypes.string,
    title: PropTypes.string,
    copy: PropTypes.string,
    image_name: PropTypes.string,
    external_link_aria: PropTypes.string,
    image: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        desktop: PropTypes.shape({
          regular: PropTypes.string,
          retina: PropTypes.string,
        }),
        mobile: PropTypes.shape({
          regular: PropTypes.string,
          retina: PropTypes.string,
        }),
      }),
    ]),
    card_image: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        desktop: PropTypes.shape({
          regular: PropTypes.string,
          retina: PropTypes.string,
        }),
        mobile: PropTypes.shape({
          regular: PropTypes.string,
          retina: PropTypes.string,
        }),
      }),
    ]),
    cta: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
      action_name: PropTypes.string,
    }),
    section_id: PropTypes.string,
    hide_section: PropTypes.bool,
  }).isRequired,
};

export default AmexPlatinumReferAFriend;
